import { AutoRenewConsent, CardOnlyNote } from '@/components/SubscriptionCard';
import { useSearchParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { toast } from 'sonner';
import { CreditCard, Info } from 'lucide-react';
import { money } from '@/lib/utils';
export default function Checkout() {
  const [params] = useSearchParams();
  if (params.get('plan') === 'plus') return <PlusCheckout defaultBilling={(params.get('billing') || 'annual').toLowerCase()} />;
  return <ProtocolCheckout />;
}

// ---------------- YourReGen+ subscription checkout ----------------
function PlusCheckout({ defaultBilling }) {
  const { user } = useAuth();
  const [billing, setBilling] = useState(defaultBilling === 'monthly' ? 'monthly' : 'annual');
  const [autoRenew, setAutoRenew] = useState(false);
  const [disp, setDisp] = useState(null);   // pricing in the customer's local currency (display)
  const [usd, setUsd] = useState(null);      // USD fallback pricing for checkout
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const country = user?.country || '';
    api.get('/pricing/landing', { params: { country, currency: localStorage.getItem('regen_currency') || undefined } }).then(r => setDisp(r.data)).catch(() => {});
    api.get('/pricing/landing', { params: { currency: 'USD' } }).then(r => setUsd(r.data)).catch(() => {});
  }, [user]);

  if (!disp) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;

  const plusDisp = disp.products?.regen_plus || {};
  const plusUsd = usd?.products?.regen_plus || {};
  const supported = !!disp.checkout_supported;

  const dispAmount = billing === 'annual' ? plusDisp.annual : plusDisp.monthly;
  const usdAmount = billing === 'annual' ? plusUsd.annual : plusUsd.monthly;

  // Charge in the local currency when supported, otherwise fall back to USD (shown explicitly).
  const chargeCurrency = supported ? disp.currency : 'USD';
  const chargeSymbol = supported ? disp.symbol : 'US$';
  const chargeAmount = supported ? dispAmount : usdAmount;

  const pay = async () => {
    if (!chargeAmount) { toast.error('Pricing unavailable. Please try again.'); return; }
    setLoading(true);
    try {
      const r = await api.post('/payments/checkout', {
        program: 'YOURREGEN_PLUS', plan_type: billing, currency: chargeCurrency,
        amount: chargeAmount, customer_email: user.email, plan: 'regen_plus', billing,
        auto_renew: billing === 'monthly' && autoRenew,
      });
      if (r.data.checkout_url) { window.location.href = r.data.checkout_url; }
      else { toast.success('YourReGen+ activated'); window.location.href = '/app/home'; }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Payment failed');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen px-6 pt-12 pb-[calc(2.5rem+var(--cookie-bar,0px))] max-w-md mx-auto bg-background text-foreground">
      <div className="label-mini text-[#00B4C7]">Final step</div>
      <h1 className="font-display font-black text-3xl mt-2 tracking-tight">Activate YourReGen+</h1>
      <p className="text-muted-foreground text-sm mt-2">Choose your plan length.</p>

      <div className="mt-8 grid grid-cols-2 gap-3">
        <BillingCard testid="plus-billing-annual" active={billing === 'annual'} onClick={() => setBilling('annual')}
          title="Annual" best amount={plusDisp.annual} symbol={disp.symbol}
          sub={plusDisp.annual_monthly_equiv ? `${money(disp.symbol, plusDisp.annual_monthly_equiv)}/mo · billed yearly` : 'Billed yearly'} />
        <BillingCard testid="plus-billing-monthly" active={billing === 'monthly'} onClick={() => setBilling('monthly')}
          title="Monthly" amount={plusDisp.monthly} symbol={disp.symbol} sub="1 month" />
      </div>
      {billing === 'monthly' && <AutoRenewConsent checked={autoRenew} onChange={setAutoRenew} testid="plus-auto-renew" />}
      <CardOnlyNote testid="plus-card-only" />

      <div className="mt-8 regen-card-elevated p-4 flex items-center justify-between border border-border rounded-2xl" data-testid="plus-total">
        <div className="label-mini">You pay now</div>
        <div className="font-mono text-2xl">{money(chargeSymbol, chargeAmount)}</div>
      </div>

      {disp.local_currency_unavailable && (
        <div className="mt-3 flex items-start gap-2 text-[12px] text-[#b57a00] bg-[#FFB800]/10 border border-[#FFB800]/30 rounded-xl p-3" data-testid="plus-currency-note">
          <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>Prices are shown and charged in US dollars (USD).</span>
        </div>
      )}

      <Button onClick={pay} disabled={loading} data-testid="plus-pay" className="mt-6 h-14 w-full bg-[#00C8FF] hover:bg-[#00C8FF]/90 text-[#081726] font-semibold rounded-2xl">
        <CreditCard className="mr-2 w-4 h-4" /> {loading ? 'Processing…' : `Subscribe · ${money(chargeSymbol, chargeAmount)}`}
      </Button>
      <p className="text-[11px] text-muted-foreground text-center mt-3">Powered by HitPay · Secure card payments</p>
    </div>
  );
}

function BillingCard({ testid, active, onClick, title, amount, symbol, sub, best }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`relative p-4 rounded-2xl border text-left transition ${active ? 'border-[#00C8FF] bg-[#00C8FF]/10' : 'border-border bg-card'}`}>
      {best && <span className="absolute -top-2.5 right-3 pill bg-[#00C8FF] text-[#081726] font-bold text-[10px]">BEST VALUE</span>}
      <div className="font-display font-bold text-base">{title}</div>
      <div className="font-mono text-xl mt-1">{money(symbol, amount)}</div>
      <div className="text-[11px] text-muted-foreground mt-1">{sub}</div>
    </button>
  );
}

// ---------------- YourReGen Protocol checkout (3 options, landing-price source) ----------------
function ProtocolCheckout() {
  const [params] = useSearchParams();
  const { user } = useAuth();
  const [disp, setDisp] = useState(null);
  const [usd, setUsd] = useState(null);
  const [selected, setSelected] = useState(() => {
    const b = (params.get('billing') || '12').toLowerCase();
    return b === '6' ? '6' : (b === 'monthly' || b === '1') ? 'monthly' : '12';
  });
  const [code, setCode] = useState('');
  const [autoRenew, setAutoRenew] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const country = user?.country || '';
    api.get('/pricing/landing', { params: { country, currency: localStorage.getItem('regen_currency') || undefined } }).then(r => setDisp(r.data)).catch(() => {});
    api.get('/pricing/landing', { params: { currency: 'USD' } }).then(r => setUsd(r.data)).catch(() => {});
  }, [user]);

  if (!disp) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;

  const pro = disp.products?.protocol || {};
  const proUsd = usd?.products?.protocol || {};
  const supported = !!disp.checkout_supported;

  // Each selectable option carries duration, price, payment model + the internal program/plan_type.
  const OPTIONS = {
    '12': { label: '12 months', model: 'Paid upfront', note: 'One-time payment for full 12-month access.', amount: pro.twelve_month, usdAmount: proUsd.twelve_month, program: 'YOURREGEN12', plan_type: 'full', best: true, equiv: pro.twelve_monthly_equiv },
    '6': { label: '6 months', model: 'Paid upfront', note: 'One-time payment for 6-month access.', amount: pro.six_month, usdAmount: proUsd.six_month, program: 'YOURREGEN6', plan_type: 'full', equiv: pro.six_monthly_equiv },
    'monthly': { label: '1 month', model: '1 month of access', note: 'One month of access. Tick automatic renewal below to be charged monthly until you cancel.', amount: pro.monthly, usdAmount: proUsd.monthly, program: 'YOURREGEN12', plan_type: 'monthly' },
  };
  const opt = OPTIONS[selected];

  const chargeCurrency = supported ? disp.currency : 'USD';
  const chargeSymbol = supported ? disp.symbol : 'US$';
  const chargeAmount = supported ? opt.amount : opt.usdAmount;

  const pay = async () => {
    if (!chargeAmount) { toast.error('Pricing unavailable. Please try again.'); return; }
    setLoading(true);
    try {
      const r = await api.post('/payments/checkout', {
        program: opt.program, plan_type: opt.plan_type, currency: chargeCurrency,
        amount: chargeAmount, customer_email: user.email, access_code: code, billing: selected,
        auto_renew: selected === 'monthly' && autoRenew,
      });
      if (r.data.checkout_url) { window.location.href = r.data.checkout_url; }
      else { toast.success('Payment confirmed'); window.location.href = '/app/home'; }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Payment failed');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen px-6 pt-12 pb-[calc(2.5rem+var(--cookie-bar,0px))] max-w-md mx-auto bg-background text-foreground">
      <div className="label-mini text-[#00B4C7]">Final step</div>
      <h1 className="font-display font-black text-3xl mt-2 tracking-tight">Activate YourReGen Protocol</h1>
      <p className="text-muted-foreground text-sm mt-2">Confirm your billing option below.</p>

      <div className="mt-7 space-y-3">
        {['12', '6', 'monthly'].map(k => {
          const o = OPTIONS[k];
          return (
            <button key={k} onClick={() => setSelected(k)} data-testid={`protocol-option-${k}`}
              className={`w-full text-left p-4 rounded-2xl border transition relative ${selected === k ? 'border-[#00C8FF] bg-[#00C8FF]/10' : 'border-border bg-card'}`}>
              {o.best && <span className="absolute -top-2.5 right-3 pill bg-[#00C8FF] text-[#081726] font-bold text-[10px]">BEST VALUE · 12 MONTHS</span>}
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-display font-bold text-base">{o.label}</div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">{o.model}{o.equiv ? ` · ${money(disp.symbol, o.equiv)}/mo equiv.` : ''}</div>
                </div>
                <div className="font-mono text-lg">{money(disp.symbol, o.amount)}{k === 'monthly' ? <span className="text-xs text-muted-foreground">/mo</span> : ''}</div>
              </div>
            </button>
          );
        })}
      </div>

      <p className="text-[12px] text-muted-foreground mt-3" data-testid="protocol-model-note">{opt.note}</p>
      {selected === 'monthly' && <AutoRenewConsent checked={autoRenew} onChange={setAutoRenew} testid="protocol-auto-renew" />}
      <CardOnlyNote testid="protocol-card-only" />

      <div className="mt-6">
        <Label htmlFor="access-code">Access Code (optional)</Label>
        <Input id="access-code" data-testid="checkout-code" value={code} onChange={e => setCode(e.target.value)} placeholder="YOURREGEN20" className="mt-1.5 bg-card border-border uppercase" />
      </div>

      <div className="mt-6 regen-card-elevated p-4 flex items-center justify-between border border-border rounded-2xl" data-testid="protocol-total">
        <div className="label-mini">You pay now</div>
        <div className="font-mono text-2xl">{money(chargeSymbol, chargeAmount)}{selected === 'monthly' ? <span className="text-sm text-muted-foreground">/mo</span> : ''}</div>
      </div>

      {disp.local_currency_unavailable && (
        <div className="mt-3 flex items-start gap-2 text-[12px] text-[#b57a00] bg-[#FFB800]/10 border border-[#FFB800]/30 rounded-xl p-3" data-testid="protocol-currency-note">
          <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>Prices are shown and charged in US dollars (USD).</span>
        </div>
      )}

      <Button onClick={pay} disabled={loading} data-testid="checkout-pay" className="mt-6 h-14 w-full bg-[#00C8FF] hover:bg-[#00C8FF]/90 text-[#081726] font-semibold rounded-2xl">
        <CreditCard className="mr-2 w-4 h-4" /> {loading ? 'Processing…' : `${selected === 'monthly' ? 'Subscribe' : 'Pay'} · ${money(chargeSymbol, chargeAmount)}`}
      </Button>
      <p className="text-[11px] text-muted-foreground text-center mt-3">Powered by HitPay · Secure card payments</p>
    </div>
  );
}
